import { ViewportScroller } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { map } from 'rxjs';
import { LeftNavPanelComponent } from './dashboard/components/left-nav-panel/left-nav-panel.component';
import { BreadcrumbComponent } from './shared/components/breadcrumb/breadcrumb.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { AuthService } from './shared/services/auth.service';
import { LayoutService } from './shared/services/layout.service';
import { LoadingService } from './shared/services/loading-service';
import { ExcludeFromTabOrderDirective } from './shared/directives/exclude-from-tab-order.directive';
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatIconModule, A11yModule, ExcludeFromTabOrderDirective, HeaderComponent, BreadcrumbComponent, LeftNavPanelComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private viewportScroller = inject(ViewportScroller);
  @ViewChild('layout', { static: true }) layout!: ElementRef;
  @ViewChild('sidebarToggle', { static: true }) sidebarToggle!: ElementRef<HTMLButtonElement>;
  loadingService = inject(LoadingService);
  private router = inject(Router);
  authService = inject(AuthService);
  isSidebarVisible = false;
  isMobile = inject(LayoutService).isMobile;
  isAuthenticated = this.authService.isAuthenticated;
  isSidebarOverlayMode = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 959.98px)')
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  toggleSidebar() {
    if (this.isSidebarVisible) {
      this.closeSidebar();
      return;
    }

    this.isSidebarVisible = true;
  }

  closeSidebar(restoreFocus = true) {
    const wasVisible = this.isSidebarVisible;
    this.isSidebarVisible = false;

    if (restoreFocus && wasVisible) {
      queueMicrotask(() => this.sidebarToggle.nativeElement.focus());
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  closeSidebarOnEscape(event: KeyboardEvent) {
    if (!this.isSidebarVisible || !this.isSidebarOverlayMode()) {
      return;
    }

    event.preventDefault();
    this.closeSidebar();
  }

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadingService.show();
        this.closeSidebar();
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.loadingService.hide();
      }
    });
  }

  scrollToTop(): void {
    const page = this.layout.nativeElement.querySelector('#page');
    if (page) {
      page.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    this.viewportScroller.scrollToPosition([0, 0]);
  }
}
